import React, { useState, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useLocation } from 'wouter';
import { 
  Search, 
  BookOpen, 
  ArrowLeft, 
  HelpCircle,
  BarChart3,
  Target,
  DollarSign,
  TrendingUp,
  Download,
  Home,
  Settings,
  Users,
  ChevronRight
} from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { ScrollArea } from '@/components/ui/scroll-area';
import ReactMarkdown from 'react-markdown';

interface HelpContent {
  id: string;
  key: string;
  content: string;
  category: string;
  updated_at: string;
}

const CATEGORY_CONFIG = {
  'Maturity Assessment': {
    icon: BarChart3,
    color: 'bg-blue-500/10 text-blue-400 border-blue-500/20',
    description: 'Assessment methodology and scoring'
  },
  'Value Engineering': {
    icon: Target,
    color: 'bg-green-500/10 text-green-400 border-green-500/20',
    description: 'Value analysis and optimization'
  },
  'ROI Analysis': {
    icon: DollarSign,
    color: 'bg-purple/10 text-purple border-purple/20',
    description: 'Financial calculations and modeling'
  },
  'Benchmarking': {
    icon: TrendingUp,
    color: 'bg-orange-500/10 text-orange-400 border-orange-500/20',
    description: 'Industry comparisons and metrics'
  },
  'Export': {
    icon: Download,
    color: 'bg-pink-500/10 text-pink-400 border-pink-500/20',
    description: 'Report generation and downloads'
  },
  'Dashboard': {
    icon: Home,
    color: 'bg-cyan-500/10 text-cyan-400 border-cyan-500/20',
    description: 'Navigation and project management'
  },
  'Admin Functions': {
    icon: Settings,
    color: 'bg-red-500/10 text-red-400 border-red-500/20',
    description: 'System administration'
  },
  'User Management': {
    icon: Users,
    color: 'bg-indigo-500/10 text-indigo-400 border-indigo-500/20',
    description: 'User access and permissions'
  },
  'General': {
    icon: HelpCircle,
    color: 'bg-gray-500/10 text-gray-400 border-gray-500/20',
    description: 'General platform guidance'
  }
};

export default function HelpCenter() {
  const [, setLocation] = useLocation();
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const [selectedArticle, setSelectedArticle] = useState<HelpContent | null>(null);

  // Fast cached help content fetch
  const { data, isLoading, error } = useQuery<HelpContent[]>({
    queryKey: ['/api/help'],
    queryFn: async () => {
      const response = await fetch('/api/help');
      if (!response.ok) {
        throw new Error('Failed to fetch help content');
      }
      return response.json();
    },
    staleTime: 10 * 60 * 1000, // 10 minutes
    gcTime: 30 * 60 * 1000, // 30 minutes (react-query v5)
    refetchOnWindowFocus: false,
  });

  const helpContent: HelpContent[] = data ?? [];

  // Debug logging
  console.log('Help Center Debug:', { 
    helpContentLength: helpContent?.length || 0, 
    isLoading, 
    error: error?.message,
    firstItem: helpContent[0]
  });

  // Fast client-side filtering
  const filteredContent = useMemo(() => {
    let filtered = helpContent;

    // Category filter
    if (selectedCategory !== 'all') {
      filtered = filtered.filter(item => item.category === selectedCategory);
    }

    // Search filter
    if (searchTerm.trim()) {
      const searchLower = searchTerm.toLowerCase();
      filtered = filtered.filter(item =>
        item.key.toLowerCase().includes(searchLower) ||
        item.content.toLowerCase().includes(searchLower) ||
        item.category.toLowerCase().includes(searchLower)
      );
    }

    return filtered;
  }, [helpContent, selectedCategory, searchTerm]);

  // Group content by category for overview
  const contentByCategory = useMemo(() => {
    return helpContent.reduce<Record<string, HelpContent[]>>((acc, item) => {
      const category = item.category || 'General';
      if (!acc[category]) acc[category] = [];
      acc[category].push(item);
      return acc;
    }, {} as Record<string, HelpContent[]>);
  }, [helpContent]);

  const formatTitle = (key: string) => {
    return key
      .split('.')
      .pop()
      ?.replace(/_/g, ' ')
      .replace(/\b\w/g, (l: string) => l.toUpperCase()) || key;
  };

  if (selectedArticle) {
    return (
      <div className="min-h-screen bg-[#0D1224] text-white">
        <div className="max-w-4xl mx-auto px-6 py-8">
          {/* Article Header */}
          <div className="mb-6">
            <Button
              variant="ghost"
              onClick={() => setSelectedArticle(null)}
              className="text-gray-400 hover:text-white mb-4"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Help Center
            </Button>
            
            <div className="flex items-center gap-3 mb-2">
              {CATEGORY_CONFIG[selectedArticle.category as keyof typeof CATEGORY_CONFIG] && (
                <div className={`p-2 rounded-lg border ${CATEGORY_CONFIG[selectedArticle.category as keyof typeof CATEGORY_CONFIG].color}`}>
                  {React.createElement(CATEGORY_CONFIG[selectedArticle.category as keyof typeof CATEGORY_CONFIG].icon, { className: "w-5 h-5" })}
                </div>
              )}
              <div>
                <h1 className="text-2xl font-bold">{formatTitle(selectedArticle.key)}</h1>
                <Badge variant="outline" className="mt-1">
                  {selectedArticle.category}
                </Badge>
              </div>
            </div>
          </div>

          {/* Article Content */}
          <Card className="bg-[#1a2332] border-gray-700">
            <CardContent className="p-8">
              <div className="prose prose-invert prose-lg max-w-none prose-headings:text-white prose-p:text-gray-300 prose-strong:text-white prose-code:text-[#4AE2C9] prose-code:bg-gray-800 prose-pre:bg-gray-900">
                <ReactMarkdown>{selectedArticle.content}</ReactMarkdown>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0D1224] text-white">
      <div className="max-w-6xl mx-auto px-6 py-8">
        {/* Header */}
        <div className="mb-8">
          <Button
            variant="ghost"
            onClick={() => setLocation('/')}
            className="text-gray-400 hover:text-white mb-6"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Dashboard
          </Button>

          <div className="flex items-center gap-3 mb-4">
            <div className="p-3 rounded-lg bg-[#4AE2C9]/10 border border-[#4AE2C9]/20">
              <BookOpen className="h-8 w-8 text-[#4AE2C9]" />
            </div>
            <div>
              <h1 className="text-3xl font-bold">Help Center</h1>
              <p className="text-gray-400 text-lg">
                Comprehensive guidance for the Redwood R2R Platform
              </p>
            </div>
          </div>
        </div>

        {/* Search and Filters */}
        <div className="mb-8 space-y-4">
          <div className="relative max-w-md">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
            <Input
              type="text"
              placeholder="Search help articles..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 bg-[#1a2332] border-gray-700 text-white placeholder-gray-400 focus:border-[#4AE2C9]"
            />
          </div>

          {/* Category Filter */}
          <div className="flex flex-wrap gap-2">
            <Button
              variant={selectedCategory === 'all' ? 'default' : 'outline'}
              size="sm"
              onClick={() => setSelectedCategory('all')}
              className={selectedCategory === 'all' ? 'bg-[#4AE2C9] text-[#0D1224]' : 'border-gray-700 text-gray-300 hover:text-white'}
            >
              All Categories
            </Button>
            {Object.keys(CATEGORY_CONFIG).map((category) => {
              const count = contentByCategory[category]?.length || 0;
              if (count === 0) return null;
              
              return (
                <Button
                  key={category}
                  variant={selectedCategory === category ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setSelectedCategory(category)}
                  className={selectedCategory === category ? 'bg-[#4AE2C9] text-[#0D1224]' : 'border-gray-700 text-gray-300 hover:text-white'}
                >
                  {category} ({count})
                </Button>
              );
            })}
          </div>
        </div>

        {/* Content */}
        {isLoading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[1, 2, 3, 4, 5, 6].map((i) => (
              <Card key={i} className="bg-[#1a2332] border-gray-700">
                <CardHeader>
                  <Skeleton className="h-6 w-3/4 bg-gray-700" />
                  <Skeleton className="h-4 w-1/2 bg-gray-700" />
                </CardHeader>
                <CardContent>
                  <Skeleton className="h-4 w-full mb-2 bg-gray-700" />
                  <Skeleton className="h-4 w-2/3 bg-gray-700" />
                </CardContent>
              </Card>
            ))}
          </div>
        ) : filteredContent.length === 0 ? (
          <div className="text-center py-12">
            <HelpCircle className="w-16 h-16 text-gray-600 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-gray-400 mb-2">No articles found</h3>
            <p className="text-gray-500">
              {searchTerm ? 'Try different search terms' : 'No help content available for this category'}
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredContent.map((article) => {
              const categoryConfig = CATEGORY_CONFIG[article.category as keyof typeof CATEGORY_CONFIG] || CATEGORY_CONFIG.General;
              const Icon = categoryConfig.icon;
              
              return (
                <Card 
                  key={article.id} 
                  className="bg-[#1a2332] border-gray-700 hover:border-[#4AE2C9]/50 transition-colors cursor-pointer group"
                  onClick={() => setSelectedArticle(article)}
                >
                  <CardHeader>
                    <div className="flex items-start justify-between">
                      <div className="flex items-center gap-3">
                        <div className={`p-2 rounded-lg border ${categoryConfig.color}`}>
                          <Icon className="w-4 h-4" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <CardTitle className="text-white text-base group-hover:text-[#4AE2C9] transition-colors">
                            {formatTitle(article.key)}
                          </CardTitle>
                          <Badge variant="outline" className="mt-1 text-xs">
                            {article.category}
                          </Badge>
                        </div>
                      </div>
                      <ChevronRight className="w-4 h-4 text-gray-500 group-hover:text-[#4AE2C9] transition-colors" />
                    </div>
                  </CardHeader>
                  <CardContent>
                    <p className="text-gray-400 text-sm line-clamp-3">
                      {article.content.replace(/[#*]/g, '').substring(0, 120)}...
                    </p>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}

        {/* Quick Stats */}
        {!searchTerm && selectedCategory === 'all' && (
          <div className="mt-12 p-6 bg-[#1a2332] rounded-lg border border-gray-700">
            <h3 className="text-lg font-semibold mb-4">Help Center Overview</h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-center">
              <div>
                <div className="text-2xl font-bold text-[#4AE2C9]">{helpContent.length}</div>
                <div className="text-sm text-gray-400">Total Articles</div>
              </div>
              <div>
                <div className="text-2xl font-bold text-[#4AE2C9]">{Object.keys(contentByCategory).length}</div>
                <div className="text-sm text-gray-400">Categories</div>
              </div>
              <div>
                <div className="text-2xl font-bold text-[#4AE2C9]">
                  {new Date(Math.max(...helpContent.map(item => new Date(item.updated_at).getTime()))).toLocaleDateString()}
                </div>
                <div className="text-sm text-gray-400">Last Updated</div>
              </div>
              <div>
                <div className="text-2xl font-bold text-[#4AE2C9]">24/7</div>
                <div className="text-sm text-gray-400">Available</div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
